import { useState, useMemo } from 'react';
import { Account } from '../types';
import AddAccountView from './AddAccountSheet';
import DraggableBottomSheet from './DraggableBottomSheet';
import ParentAccountCard from './ParentAccountCard';
import {
  FiCreditCard,
} from 'react-icons/fi';
import Card from './Card';
import { useAppSelector } from '../store/hooks';
import AccountCardSkeleton from './skeletons/AccountCardSkeleton';
import { BsWindowPlus } from 'react-icons/bs';
import { useNavigation } from '../hooks/useNavigation';
import { useRouteScrollContainer } from '../hooks/useRouteScrollContainer';

// Define props for the component
interface AccountsDisplayCardProps {
  title: string;
  accountTypes: Account['type'][]; // Array of account types to display
  emptyStateMessage: string;
}

const parseLocalDate = (date?: string | null) => {
  if (!date) return null;
  const parsed = new Date(`${date}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getLocalToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const getDaysUntil = (date?: string | null) => {
  const targetDate = parseLocalDate(date);
  if (!targetDate) return null;
  const today = getLocalToday();
  return Math.floor((targetDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};

const addMonthsToLocalDateString = (date?: string | null, months = 1) => {
  const parsed = parseLocalDate(date);
  if (!parsed) return null;
  parsed.setMonth(parsed.getMonth() + months);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCreditCardBillPriority = (account: Account) => {
  if (account.type !== 'CREDIT_CARD' || !account.creditCardBill) {
    return 0;
  }

  const bill = account.creditCardBill;
  const daysUntilDue = getDaysUntil(bill.dueDate);
  if (bill.status === 'OVERDUE' || (bill.status !== 'PAID' && daysUntilDue != null && daysUntilDue < 0)) {
    return 3;
  }

  if (bill.status !== 'PAID' && daysUntilDue != null && daysUntilDue <= 1) {
    return 2;
  }

  const daysUntilStatement = getDaysUntil(addMonthsToLocalDateString(bill.statementDate));
  if (bill.status === 'PAID' && daysUntilStatement != null && daysUntilStatement < 0) {
    return 2;
  }

  return 0;
};

function AccountsDisplayCard({ title, accountTypes, emptyStateMessage }: AccountsDisplayCardProps) {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { navigateTo } = useNavigation();
  const { accounts: allAccounts, status, error } = useAppSelector(state => state.accounts);
  const isInitialLoading = (status === 'loading' || status === 'idle') && allAccounts.length === 0;
  const scrollContainerId = useMemo(() => {
    const accountTypeKey = [...accountTypes].sort().join(',');
    return `screen:home:accounts-display:${title}:${accountTypeKey}`;
  }, [accountTypes, title]);
  const setRouteScrollContainer = useRouteScrollContainer<HTMLDivElement>(scrollContainerId);

  const accounts = useMemo(() => {
    const sortedAccounts = allAccounts
      .filter(acc => accountTypes.includes(acc.type))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    const priorityAccounts = sortedAccounts
      .filter(account => getCreditCardBillPriority(account) > 0)
      .sort((a, b) => {
        const priorityDifference = getCreditCardBillPriority(b) - getCreditCardBillPriority(a);
        if (priorityDifference !== 0) return priorityDifference;

        const aDueTime = parseLocalDate(a.creditCardBill?.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bDueTime = parseLocalDate(b.creditCardBill?.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return aDueTime - bDueTime;
      });

    if (priorityAccounts.length === 0) {
      return sortedAccounts;
    }

    const priorityAccountIds = new Set(priorityAccounts.map(account => account.id));
    const normalAccounts = sortedAccounts.filter(account => !priorityAccountIds.has(account.id));
    return [...priorityAccounts, ...normalAccounts];
  }, [allAccounts, accountTypes]);

  const groupedAccounts = useMemo(() => {
    const parents = accounts.filter(acc => acc.parentAccountId === null || acc.parentAccountId === undefined);
    const childrenMap = accounts.reduce((acc, current) => {
      if (current.parentAccountId) {
        if (!acc[current.parentAccountId]) {
          acc[current.parentAccountId] = [];
        }
        acc[current.parentAccountId].push(current);
      }
      return acc;
    }, {} as Record<string, Account[]>);

    return parents.map(parent => ({
      ...parent,
      children: childrenMap[parent.id] || []
    }));
  }, [accounts]);

  const openSheet = () => setIsSheetOpen(true);
  const closeSheet = () => setIsSheetOpen(false);

  const handleAccountCreated = (newAccount: Account) => {
    closeSheet();
  };

  const handleAccountClick = (account: Account, isChild: boolean = false) => {
    navigateTo(`/accounts/${account.id}`, { state: { account, isChild } });
  };

  return (
    <Card>
      <header className="top-0 p-4 pb-3 flex items-center justify-between">
        <h1 className="text-sm text-muted-foreground font-bold">{title}</h1>
        <button onClick={openSheet} className="text-primary font-bold text-xl">
          <BsWindowPlus />
        </button>
      </header>

      <div
        ref={setRouteScrollContainer}
        className="flex-grow overflow-x-auto px-2 gap-2 pb-4 pt-0 flex whitespace-nowrap"
        style={{ scrollbarWidth: 'none' }}
      >
        {isInitialLoading && (
          <>
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </>
        )}

        {!isInitialLoading && !error && groupedAccounts.map(parentAccount => (
          <ParentAccountCard
            key={parentAccount.id}
            parentAccount={parentAccount}
            onCardClick={(account, isChild) => handleAccountClick(account, isChild)}
          />
        ))}

        {!isInitialLoading && !error && accounts.length === 0 && (
          <div className="text-center py-8 flex-shrink-0" style={{ width: '100%' }}>
            <FiCreditCard className="h-12 w-12 mb-4 mx-auto text-muted-foreground" />
            <p className="text-muted-foreground">{emptyStateMessage}</p>
          </div>
        )}
      </div>

      {/* Add Account Bottom Sheet */}
      <DraggableBottomSheet
        isOpen={isSheetOpen}
        onClose={closeSheet}
        title={"Add New Account"}
      >
        {isSheetOpen ? (
          <AddAccountView
            onAccountCreated={handleAccountCreated}
            availableParentAccounts={groupedAccounts}
            allowedAccountTypes={accountTypes}
            prefilledAccountType={accountTypes.length === 1 ? accountTypes[0] : undefined}
          />
        ) : null}
      </DraggableBottomSheet>
    </Card>
  );
}

export default AccountsDisplayCard; 
